import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Backdrop,
  Button,
  IconButton,
  Modal,
} from "@mui/material";
import {
  findGetParameter,
  isUsingWS,
  newPath,
  postData,
} from "../../components/utilities/UtilityFunctions";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import EventRoster from "./components/EventRoster";
import EventPlacings from "./components/EventPlacings";
import EventPairings from "./components/EventPairings";
import EventSoftScores from "./components/EventSoftScores";

import { getEvent, getMenuItems } from "./components/EventFunctions";

import { useConfirm } from "material-ui-confirm";
import { Formik } from "formik";
import GenericPicker from "../../components/utilities/GenericPicker";
import ShareModal from "../../components/modals/ShareModal";
import EventUserRoles from "./components/EventUserRoles";
import EventJudgeReport from "./components/EventJudgeReport";
import EventAccessCodes from "./components/EventAccessCodes";
import EventBrackets from "./components/EventBrackets";
import EventOrganizeTitle from "./components/EventOrganizeTitle";
import { BCPXmark } from "../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import EventPlacingsWS from "./components/EventPlacings/EventPlacingsWS";
import EventRosterWS from "./components/EventRoster/EventRosterWS";
import EventCreation from "./components/EventCreation";
import EventPairingsWS from "./components/EventPairings/EventPairingsWS";
import Redirect from "../../components/utilities/Redirect";
import EventInfo from "./components/EventInfo";
import { setCookie } from "../../components/layout/cookies/CookieFunctions";
import { getTabOrganizer } from "./utils/tab";

export const tabIndexes = {
  roster: 0,
  pairings: 1,
  brackets: 2,
  placings: 3,
  "soft-scores": 4,
  info: 5,
  details: 6,
  config: 7,
  registration: 8,
  circuits: 9,
  users: 10,
  "judge-report": 11,
  "access-codes": 12,
};

export default function EventOrganize(props) {
  let { eventId } = useParams();
  let { setItemsMiddle } = props;
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [redirect, setRedirect] = useState(null);

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const useWebSockets = isUsingWS(authContext);

  const activeTabParam = findGetParameter("active_tab");

  const [activeTab, setActiveTab] = useState(
    activeTabParam
      ? tabIndexes[activeTabParam.toLowerCase()] !== undefined
        ? tabIndexes[activeTabParam.toLowerCase()]
        : null
      : null
  );

  const handleTabChange = async (e, newTab, freshEvent) => {
    setLoading(true);
    try {
      await hasAccess(freshEvent || event, authContext, newTab, true);
    } catch (err) {
      setLoading(false);
      return;
    }

    var path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newTab) {
        path = newPath({
          active_tab: tabIndex,
          round: null,
        });

        return true;
      }
    });
    setActiveTab(newTab);
    navigate(path, { replace: false });
    setLoading(false);
  };

  const handleRefreshEvent = async (newTab) => {
    var freshEvent = await getEvent(eventId, authContext, {
      player: false,
      ticketing:
        (newTab !== undefined ? newTab : activeTab) === tabIndexes.details,
      role: true,
      overview: true,
      circuits: true,
    });

    if (
      freshEvent.ownerId !== authContext?.authState?.identity?.userId &&
      authContext?.authState?.identity?.isAdmin !== true &&
      !authContext?.authState?.identity?.adminGameSystems?.includes(
        freshEvent?.gameSystemId
      )
    ) {
      if (!freshEvent.userRole?.permissions) {
        throw new Error("Access denied");
      }

      if (
        ((newTab !== undefined ? newTab : activeTab) === tabIndexes.users &&
          freshEvent.userRole.permissions.eventUsers === undefined) ||
        ((newTab !== undefined ? newTab : activeTab) === tabIndexes.details &&
          freshEvent.userRole.permissions.eventDetails === undefined)
      ) {
        let err = new Error("Access denied");
        err.name = "Event User Error";
        throw err;
      }
    }

    setEvent(freshEvent);
    if (newTab !== undefined) {
      await handleTabChange(null, newTab, freshEvent);
    }
    return freshEvent;
  };

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      try {
        if (!eventId) {
          setActiveTab(tabIndexes.details);
        } else {
          var freshEvent = await handleRefreshEvent();

          if (activeTab === null && freshEvent) {
            const smartTab = getTabOrganizer(
              freshEvent,
              tabIndexes,
              activeTabParam
            );
            setActiveTab(smartTab);

            var path;
            Object.keys(tabIndexes).forEach((tabIndex) => {
              if (tabIndexes[tabIndex] === smartTab) {
                const params = {
                  active_tab: tabIndex
                };
                if (
                  smartTab === tabIndexes.pairings &&
                  freshEvent?.currentRound
                ) {
                  params.round = freshEvent.currentRound;
                }
                path = newPath(params);
                return true;
              }
            });
            if (path) {
              navigate(path, { replace: true });
            }
          }

          var middleItems = getMenuItems(
            freshEvent,
            authContext,
            setResetModalOpen,
            setShareModalOpen
          );

          for (const key in middleItems) {
            if (middleItems[key] === null) {
              delete middleItems[key];
            }
          }

          if (authContext?.authState?.cookieConsent) {
            setCookie("lastEventId", freshEvent.id);
          }

          setItemsMiddle(middleItems);
        }
      } catch (err) {
        if (err.name === "Event User Error") {
          setLoading(false);
          postNotification("error", err);
          setActiveTab(null);
          navigate(`/organize/event/${eventId}`);
        } else {
          setRedirect("/event/:eventId");
        }
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}

      {/* Redirects user to player-facing event page if "redirect" variable is set to a specific path */}
      {redirect && <Redirect path={redirect} />}

      <Box sx={{ width: "100%" }}>
        {activeTab === null || activeTab === tabIndexes.info ? (
          <EventInfo
            organize
            event={event}
            handleTabChange={handleTabChange}
            setActiveTab={setActiveTab}
            authContext={authContext}
            activeTab={activeTab}
          />
        ) : activeTab === tabIndexes.details ||
          activeTab === tabIndexes.config ||
          activeTab === tabIndexes.registration ||
          activeTab === tabIndexes.circuits ? (
          <></>
        ) : (
          <EventOrganizeTitle eventName={event?.name} />
        )}
        {event && activeTab === tabIndexes.roster && useWebSockets ? (
          <EventRosterWS
            organize
            event={event}
            handleTabChange={handleTabChange}
            setActiveTab={setActiveTab}
            authContext={authContext}
            activeTab={activeTab}
          />
        ) : event && activeTab === tabIndexes.roster ? (
          <EventRoster
            organize
            event={event}
            handleTabChange={handleTabChange}
            setActiveTab={setActiveTab}
            authContext={authContext}
            activeTab={activeTab}
          />
        ) : (
          <></>
        )}

        {event && activeTab === tabIndexes.pairings && (
          <>
            {useWebSockets ? (
              <EventPairingsWS
                organize
                event={event}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
                setEvent={setEvent}
                setResetModalOpen={setResetModalOpen}
              />
            ) : (
              <EventPairings
                organize
                event={event}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
                setEvent={setEvent}
                setResetModalOpen={setResetModalOpen}
              />
            )}
          </>
        )}
        {event && activeTab === tabIndexes.brackets && (
          <EventBrackets
            organize
            event={event}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
            setEvent={setEvent}
            setResetModalOpen={setResetModalOpen}
          />
        )}
        {event && activeTab === tabIndexes.placings && (
          <>
            {useWebSockets ? (
              <EventPlacingsWS
                organize
                event={event}
                setEvent={setEvent}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
                setResetModalOpen={setResetModalOpen}
              />
            ) : (
              <EventPlacings
                organize
                event={event}
                setEvent={setEvent}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
                setResetModalOpen={setResetModalOpen}
              />
            )}
          </>
        )}
        {event && activeTab === tabIndexes["soft-scores"] && (
          <EventSoftScores
            organize
            event={event}
            setEvent={setEvent}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        )}
        {(!eventId || event) &&
          (activeTab === tabIndexes.details ||
            activeTab === tabIndexes.config ||
            activeTab === tabIndexes.registration ||
            activeTab === tabIndexes.circuits) && (
            <EventCreation
              event={event}
              setEvent={setEvent}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleTabChange={handleTabChange}
              authContext={authContext}
              step={
                activeTab === tabIndexes.circuits
                  ? 5
                  : activeTab === tabIndexes.registration
                    ? 4
                    : activeTab === tabIndexes.config
                      ? 3
                      : 2
              }
            />
          )}
        {event && activeTab === tabIndexes.users && (
          <EventUserRoles
            organize
            event={event}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        )}
        {event && activeTab === tabIndexes["judge-report"] && (
          <EventJudgeReport
            organize
            event={event}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        )}
        {event && activeTab === tabIndexes["access-codes"] && (
          <EventAccessCodes
            organize
            event={event}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        )}
        <ResetModal
          event={event}
          open={resetModalOpen}
          setOpen={setResetModalOpen}
          setLoading={setLoading}
          setActiveTab={setActiveTab}
          handleRefreshEvent={handleRefreshEvent}
        />
      </Box>
      {event && (
        <ShareModal
          parentOpen={shareModalOpen}
          setParentOpen={setShareModalOpen}
          url={`${window.location.origin}/event/${event?.id}`}
          shareOptions={{
            twitter: `http://twitter.com/share?text=Checkout ${event?.name}${
              getEnvironmentVariable("COMPANY_NAME") === "Best Coast Pairings"
                ? " @BCPairings"
                : ""
            }&url=${window.location.origin}/event/${
              event?.id
            }&hashtags=${getEnvironmentVariable("COMPANY_NAME")
              .split(" ")
              .join("")},${event?.gameSystem?.name?.split(" ")?.join("")}`,
            email: `mailto:?subject=Checkout ${
              event?.name
            } at ${getEnvironmentVariable("COMPANY_NAME")}&body=Checkout ${
              event?.name
            } at ${getEnvironmentVariable("COMPANY_NAME")} ${
              window.location.origin
            }/event/${event?.id}`,
          }}
        />
      )}
    </>
  );
}

export async function hasAccess(event, authContext, tabIndex, notify) {
  var err;

  if (
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    )
  ) {
    return true;
  }

  if (event) {
    if (authContext?.authState?.identity?.userId === event.ownerId) {
      return true;
    }

    if (event.tournamentOrganizers) {
      for (var to = 0; to < event.tournamentOrganizers.length; to++) {
        if (
          authContext?.authState?.identity?.userId ===
          event.tournamentOrganizers[to].objectId
        ) {
          return true;
        }
      }
    }
  } else {
    return true;
  }

  err = new Error("Only Tournament Organizers have access to this function");
  if (notify) postNotification("warning", err);

  if (err) {
    throw err;
  }
  return false;
}

function ResetModal(props) {
  const { event, open, setOpen, setLoading, setActiveTab } = props;
  const [resetOptions, setResetOptions] = useState(null);
  const confirm = useConfirm();
  const navigate = useNavigate();

  useEffect(() => {
    var optionsArray = [{ value: 0, label: "Beginning" }];
    var maxRound = event?.ended ? event?.currentRound : event?.currentRound - 1;
    for (var i = 1; i <= maxRound; i++) {
      optionsArray.push({
        value: i,
        label: `Round ${i}`,
      });
    }
    setResetOptions(optionsArray);
  }, [event]);

  if (!event || !open) return <></>;

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      {event ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                Reset To
              </Typography>
            </Grid>
          </Grid>

          <Formik
            initialValues={{
              round: "",
            }}
            enableReinitialize
            onSubmit={async (values) => {
              if (
                values.round === undefined ||
                values.round === null ||
                values.round === ""
              ) {
                postNotification("error", "Please select a reset value");
                return;
              }
              try {
                await confirm({
                  title: `Reset Event.`,
                  description: `Are you sure you want to reset your Event back to ${
                    values.round === 0
                      ? "the Beginning"
                      : `Round ${values.round}`
                  }?`,
                  dialogProps: {
                    sx: {
                      zIndex: 99999,
                    },
                  },
                });
              } catch (err) {
                return;
              }
              try {
                setLoading(true);
                await postData(`/events/${event.id}/resetRound`, {
                  body: { round: values.round },
                });
                navigate(`/organize/event/${event.id}`, { replace: true });

                setActiveTab(null);
                setOpen(false);
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
              return;
            }}
          >
            {({ handleChange, handleSubmit, values }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <GenericPicker
                      label="Reset To"
                      required
                      inclAny={false}
                      name="round"
                      list={resetOptions || []}
                      value={values.round}
                      handleChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
